import React, { useState } from "react";
import { Modal, Box, Backdrop, Paper, Typography, Button, Fade, IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import AttachFile from "@material-ui/icons/AttachFile";
import DescriptionIcon from '@material-ui/icons/Description';
import DocumentApiClient from "../../../api/DocumentApiClient"; 

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    paper: {
        padding: theme.spacing(6),
        maxWidth: 600,
        width: '100%'
    },
    uploadArea: {
        border: '2px dashed #ccc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
        textAlign: 'center',
        cursor: 'pointer',
        marginBottom: theme.spacing(2),
    },
    uploadIcon: {
        fontSize: '4rem',
        color: "#ccc",
        marginBottom: theme.spacing(2)
    },
    buttonContainer: {
        marginTop: theme.spacing(4),
        display: 'flex',
        justifyContent: 'space-between'
    },
    button: {
        marginTop: theme.spacing(2)
    },
    boldText: {
        fontWeight: 'bold',
        fontSize: '1rem'
    }
}));

interface UploadModalProps {
    open: boolean;
    handleClose: () => void;
    currentType: 'decision' | 'carrier' | 'student' | 'authorized';
    handleUpload: (files: FileList | null) => void; 
    financingRequestId?: number;
    schoolCarrierId?:  number | null;
    authorizedCarrierId?:  number | null;
    financingRequestStudentDataId?:  number | null;
    alreadyUploadedDocumentCount:number
}

const UploadModal: React.FC<UploadModalProps> = ({ open, handleClose, currentType, financingRequestId, schoolCarrierId, authorizedCarrierId, financingRequestStudentDataId, alreadyUploadedDocumentCount }) => {
    const classes = useStyles();
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const documentApiClient = new DocumentApiClient();

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const filesArray = Array.from(event.target.files);
            
            const pdfFiles = filesArray.filter(file => file.type === 'application/pdf');
            if (pdfFiles.length !== filesArray.length) {
                alert('Dozvoljen je samo dokument u .pdf formatu');
                event.target.files = null;
                return;
            } else {
                for (const file of pdfFiles) {
                    if(file.size > 5000000){
                        alert('Nije dopušten upload dokumenta većeg od 5MB.');
                        event.target.files = null;
                        return;
                    }
                }
            }

            let maxFilesAllowed = 0;
            if(currentType === 'decision'){
                maxFilesAllowed = 1 - alreadyUploadedDocumentCount;
            } else if (currentType === 'student') {
                maxFilesAllowed = 3 - alreadyUploadedDocumentCount;
            } else {
                maxFilesAllowed = 5 - alreadyUploadedDocumentCount;
            }
            if (pdfFiles.length + selectedFiles.length > maxFilesAllowed) {
                let porukaDokumentMnozina = maxFilesAllowed == 1 ? 'dokument' : 'dokumenata';
                alert(`Možete učitati još najviše ${maxFilesAllowed} ${porukaDokumentMnozina}.`);
                return;
            }
    
            setSelectedFiles(prevFiles => [...prevFiles, ...pdfFiles]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    const onUpload = async () => {
        if (selectedFiles.length === 0) {
            alert('Molimo učitajte dokument.');
            return;
        }
        for (const file of selectedFiles) {
            try {
                if (currentType === 'decision') {
                    if (!financingRequestId) {
                        return;
                    }
                    await documentApiClient.uploadDecisionPdf(file, financingRequestId);
                } else if (currentType === 'student') {
                    if (!financingRequestStudentDataId || !financingRequestId) {
                        return;
                    }
                    await documentApiClient.uploadStudentPdf(file, financingRequestStudentDataId, financingRequestId);
                } else if (currentType === 'carrier') {
                    if (!schoolCarrierId || !financingRequestId) {
                        return;
                    }
                    await documentApiClient.uploadSchoolCarrierPdf(file, financingRequestId, schoolCarrierId);
                }
                else if ( currentType === 'authorized') {
                    if (!authorizedCarrierId || !financingRequestId) {
                        return;
                    }
                    await documentApiClient.uploadAuthorizedCarrierPdf(file, financingRequestId, authorizedCarrierId);
                }
            } catch (error) {
                console.error('Error uploading document:', error); 
                alert('Error uploading document.');
            }
        }
        
        setSelectedFiles([]);
        handleClose();
    };

    const handleCancel = () => {
        setSelectedFiles([]);
        handleClose();
    };

    return (
        <Modal
            aria-labelledby="upload-modal-title"
            aria-describedby="upload-modal-description"
            className={classes.modal}
            open={open}
            onClose={handleCancel}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Paper className={classes.paper}>
                    <Typography id="upload-modal-title" className={classes.boldText}>
                        Učitavanje dokumenta
                    </Typography>
                    <Typography variant="body2" id="upload-modal-description">
                        Molimo priložite jedan ili više dokumenata.
                    </Typography>
                    <label className={classes.uploadArea}>
                        <input type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} />
                        <DescriptionIcon className={classes.uploadIcon}/>
                        <Typography variant="body2">
                            Povucite datoteku ili kliknite ovdje za učitavanje dokumenta.
                        </Typography>
                    </label>
                    <Typography variant="body2">
                        *Dozvoljen je samo dokument u .pdf formatu.
                    </Typography>
                    <Typography variant="body2">
                        *Dokument može biti velik do 5MB.
                    </Typography>
                    <Typography variant="body2">
                        *Možete učitati još najviše {
                            currentType === 'decision' ? 
                                (1 - alreadyUploadedDocumentCount) + ' dokument' 
                            : currentType === 'student' ?
                                (3 - alreadyUploadedDocumentCount) + ' dokumenata' 
                            : 
                                (5 - alreadyUploadedDocumentCount) + ' dokumenata'
                        }.
                    </Typography>
                    <Typography className={classes.boldText}>
                        Priloženi dokumenti:
                    </Typography>
                    {selectedFiles.length > 0 && (
                        <div>
                            {selectedFiles.map((file, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                                    <a href={URL.createObjectURL(file)} target="_blank" rel="noopener noreferrer">
                                      {file.name}
                                    </a>
                                    <IconButton 
                                        onClick={() => handleRemoveFile(index)} 
                                        size="small" 
                                        style={{ marginLeft: '5px', color: 'red' }}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className={classes.buttonContainer}>
                        <Button variant="contained" color="primary" onClick={handleCancel} className={classes.button}>
                            Odustani
                        </Button>
                        <Button variant="contained" color="primary" onClick={onUpload} className={classes.button}>
                            Dodaj
                        </Button>
                    </div>          
                </Paper>
            </Fade>
        </Modal>
    );
};

export default UploadModal;
